/**
 * useWebcam Hook
 *
 * Provides webcam access via MediaStream API.
 * Fresh implementation for SLAM S&C Movement Analysis (Phase 3).
 */
import { useState, useRef, useCallback, useEffect } from 'react';

export function useWebcam() {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [isWebcamOn, setIsWebcamOn] = useState(false);
  const [error, setError] = useState(null);

  const startWebcam = useCallback(async (constraints = { video: true, audio: false }) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setIsWebcamOn(true);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Webcam error:', err);
    }
  }, []);

  const stopWebcam = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setIsWebcamOn(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopWebcam();
  }, [stopWebcam]);

  return { videoRef, streamRef, isWebcamOn, error, startWebcam, stopWebcam };
}
